import { useMemo, useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  FormHelperText,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import {
  DATE_FORMAT,
  LOCALE,
  MAX_DASHBOARD_RANGE_DAYS,
  ERROR_DASHBOARD_RANGE_EXCEEDED,
  ERROR_FROM_GREATER_THAN_TO,
  COUNTRIES,
  COUNTRY_ARG,
} from "../../utils/constants";
import {
  IDashboardFilters,
  IDashboardFilterOptions,
} from "../../interfaces/dashboard";
import { IProvinceLabel } from "../../utils/common";

// Cada campo llena el 100% de su celda; el ancho real y las columnas los define
// el grid contenedor (FIELDS_GRID_SX).
const FIELD_SX = { width: "100%" };

const FIELDS_GRID_SX = {
  flex: 1,
  width: { xs: "100%", md: "auto" },
  display: "grid",
  gap: 2,
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(auto-fill, 160px)",
  },
};

interface IDashboardFilterBarProps {
  filters: IDashboardFilters;
  options: IDashboardFilterOptions;
  // El filtro de Merchant solo aplica a marketplaces (agrupan varios merchants).
  showMerchantFilter: boolean;
  // El filtro de Cliente solo aplica al usuario interno (cross-entidad).
  showClientFilter: boolean;
  // El filtro de País solo aplica al usuario interno (cross-país).
  showCountryFilter: boolean;
  onApply: (_newFilters: Partial<IDashboardFilters>) => void;
  onClear: () => void;
  onExportPdf?: () => void;
  exporting?: boolean;
  // Etiqueta de la división geográfica según el país (Provincia/Departamento).
  provinceLabel: IProvinceLabel;
}

interface IStatusOption {
  id: string;
  label: string;
}

interface IOption {
  id: string;
  label: string;
}

// Lista completa fija: fallback cuando el backend aún no devolvió datos o el
// backfill no corrió (evita un selector vacío).
const ALL_COUNTRY_OPTIONS: IOption[] = Object.entries(COUNTRIES).map(
  ([id, label]) => ({ id, label }),
);

// Deriva las opciones de países dinámicos (presentes en el período). Si el
// backend no devuelve ninguno (sin datos o sin backfill), muestra todos como
// fallback para no bloquear al usuario.
const buildCountryOptions = (countries: string[]): IOption[] => {
  if (!countries.length) return ALL_COUNTRY_OPTIONS;
  return countries
    .filter((c) => c in COUNTRIES)
    .map((c) => ({ id: c, label: COUNTRIES[c] }));
};

const parseDate = (value?: string): DateTime | null => {
  if (!value) return null;
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt : null;
};

// Los estados llegan ya traducidos desde el backend (fuente de verdad única).
// value = key crudo que se reenvía como filtro; label = texto en español.
// Deduplica por value.
const buildStatusOptions = (
  statuses: { value: string; label: string }[],
): IStatusOption[] => {
  const seen = new Map<string, IStatusOption>();
  statuses.forEach(({ value, label }) => {
    if (!seen.has(value)) seen.set(value, { id: value, label });
  });
  return Array.from(seen.values());
};

interface IDateRangeFieldsProps {
  fromDate: DateTime | null;
  toDate: DateTime | null;
  invalidOrder: boolean;
  rangeExceeded: boolean;
  onFromChange: (_value: DateTime | null) => void;
  onToChange: (_value: DateTime | null) => void;
}

const DateRangeFields = ({
  fromDate,
  toDate,
  invalidOrder,
  rangeExceeded,
  onFromChange,
  onToChange,
}: IDateRangeFieldsProps) => (
  <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={LOCALE}>
    <DatePicker
      label="Desde"
      value={fromDate}
      disableFuture
      inputFormat={DATE_FORMAT}
      onChange={(value) => {
        if (!value || value.isValid) onFromChange(value);
      }}
      renderInput={(params) => (
        <TextField {...params} size="small" error={invalidOrder} sx={FIELD_SX} />
      )}
    />
    <DatePicker
      label="Hasta"
      value={toDate}
      disableFuture
      minDate={fromDate ?? undefined}
      maxDate={fromDate ? fromDate.plus({ days: MAX_DASHBOARD_RANGE_DAYS }) : undefined}
      inputFormat={DATE_FORMAT}
      onChange={(value) => {
        if (!value || value.isValid) onToChange(value);
      }}
      renderInput={(params) => (
        <TextField {...params} size="small" error={invalidOrder || rangeExceeded} sx={FIELD_SX} />
      )}
    />
  </LocalizationProvider>
);

interface IDimensionFiltersProps {
  options: IDashboardFilterOptions;
  statusOptions: IStatusOption[];
  merchantOptions: IOption[];
  clientOptions: IOption[];
  showMerchantFilter: boolean;
  showClientFilter: boolean;
  showCountryFilter: boolean;
  carrierId?: string;
  status?: string;
  province?: string;
  merchantId?: string;
  clientId?: string;
  country?: string;
  provinceLabel: IProvinceLabel;
  onCarrierChange: (_id?: string) => void;
  onStatusChange: (_id?: string) => void;
  onProvinceChange: (_name?: string) => void;
  onMerchantChange: (_id?: string) => void;
  onClientChange: (_id?: string) => void;
  onCountryChange: (_id: string) => void;
}

// Las opciones las provee el backend según los datos reales del período (N >= 1).
// Merchant solo para marketplaces; Cliente y País solo para el usuario interno.
const DimensionFilters = ({
  options,
  statusOptions,
  merchantOptions,
  clientOptions,
  showMerchantFilter,
  showClientFilter,
  showCountryFilter,
  carrierId,
  status,
  province,
  merchantId,
  clientId,
  country,
  provinceLabel,
  onCarrierChange,
  onStatusChange,
  onProvinceChange,
  onMerchantChange,
  onClientChange,
  onCountryChange,
}: IDimensionFiltersProps) => {
  const selectedCarrier =
    options.carriers.find((c) => c.carrier_id === carrierId) ?? null;
  const selectedStatus = statusOptions.find((s) => s.id === status) ?? null;
  const selectedMerchant =
    merchantOptions.find((m) => m.id === merchantId) ?? null;
  const selectedClient = clientOptions.find((c) => c.id === clientId) ?? null;
  const countryOptions = buildCountryOptions(options.countries ?? []);
  const selectedCountry =
    countryOptions.find((c) => c.id === country) ??
    countryOptions.find((c) => c.id === COUNTRY_ARG) ??
    null;

  return (
    <>
      {showCountryFilter && (
        <Autocomplete
          size="small"
          sx={FIELD_SX}
          disableClearable
          options={countryOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedCountry ?? undefined}
          onChange={(_, value) => {
            if (value) onCountryChange(value.id);
          }}
          renderInput={(params) => (
            <TextField {...params} label="País" />
          )}
        />
      )}
      {showClientFilter && (
        <Autocomplete
          size="small"
          sx={FIELD_SX}
          options={clientOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedClient}
          noOptionsText="Sin clientes en el período"
          onChange={(_, value, reason) =>
            onClientChange(reason === "clear" ? undefined : value?.id)
          }
          renderInput={(params) => (
            <TextField {...params} label="Cliente" placeholder="Todos" />
          )}
        />
      )}
      <Autocomplete
        size="small"
        sx={FIELD_SX}
        options={options.carriers}
        getOptionLabel={(option) => option.carrier_name}
        isOptionEqualToValue={(option, value) =>
          option.carrier_id === value.carrier_id
        }
        value={selectedCarrier}
        noOptionsText="Sin operadores en el período"
        onChange={(_, value, reason) =>
          onCarrierChange(reason === "clear" ? undefined : value?.carrier_id)
        }
        renderInput={(params) => <TextField {...params} label="Operador" />}
      />
      <Autocomplete
        size="small"
        sx={FIELD_SX}
        options={statusOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedStatus}
        noOptionsText="Sin estados en el período"
        onChange={(_, value, reason) =>
          onStatusChange(reason === "clear" ? undefined : value?.id)
        }
        renderInput={(params) => <TextField {...params} label="Estado" />}
      />
      {showMerchantFilter && (
        <Autocomplete
          size="small"
          sx={FIELD_SX}
          options={merchantOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedMerchant}
          noOptionsText="Sin merchants en el período"
          onChange={(_, value, reason) =>
            onMerchantChange(reason === "clear" ? undefined : value?.id)
          }
          renderInput={(params) => <TextField {...params} label="Merchant" />}
        />
      )}
      <Autocomplete
        size="small"
        sx={FIELD_SX}
        options={options.provinces}
        value={province ?? null}
        noOptionsText={`Sin ${provinceLabel.plural} en el período`}
        onChange={(_, value, reason) =>
          onProvinceChange(reason === "clear" ? undefined : value ?? undefined)
        }
        renderInput={(params) => (
          <TextField {...params} label={provinceLabel.singular} />
        )}
      />
    </>
  );
};

interface IFilterActionButtonsProps {
  onClear: () => void;
  onApply: () => void;
  disableApply: boolean;
  onExportPdf?: () => void;
  exporting?: boolean;
}

const FilterActionButtons = ({
  onClear,
  onApply,
  disableApply,
  onExportPdf,
  exporting,
}: IFilterActionButtonsProps) => (
  <Stack direction="row" spacing={1} flexWrap="wrap">
    <Button variant="outlined" startIcon={<ClearIcon />} onClick={onClear} size="small">
      Limpiar
    </Button>
    <Button
      variant="contained"
      startIcon={<SearchIcon />}
      onClick={onApply}
      disabled={disableApply}
      size="small"
      sx={{ "& .MuiSvgIcon-root": { color: "#fff" } }}
    >
      Buscar
    </Button>
    <Button
      variant="outlined"
      startIcon={
        exporting ? <CircularProgress size={16} /> : <DownloadIcon />
      }
      size="small"
      onClick={onExportPdf}
      disabled={!onExportPdf || exporting}
    >
      {exporting ? "Generando…" : "Descargar PDF"}
    </Button>
  </Stack>
);

const DashboardFilterBar = ({
  filters,
  options,
  showMerchantFilter,
  showClientFilter,
  showCountryFilter,
  onApply,
  onClear,
  onExportPdf,
  exporting,
  provinceLabel,
}: IDashboardFilterBarProps) => {
  const statusOptions = useMemo(
    () => buildStatusOptions(options.statuses),
    [options.statuses],
  );
  const merchantOptions = useMemo<IOption[]>(
    () =>
      options.merchants.map((m) => ({
        id: String(m.merchant_id),
        label: m.merchant_name,
      })),
    [options.merchants],
  );
  const clientOptions = useMemo<IOption[]>(
    () =>
      options.clients.map((c) => ({
        id: String(c.client_id),
        label: c.clientName,
      })),
    [options.clients],
  );

  const [fromDate, setFromDate] = useState<DateTime | null>(parseDate(filters.from));
  const [toDate, setToDate] = useState<DateTime | null>(parseDate(filters.to));
  const [carrierId, setCarrierId] = useState<string | undefined>(filters.carrier_id);
  const [status, setStatus] = useState<string | undefined>(filters.status);
  const [province, setProvince] = useState<string | undefined>(filters.province);
  const [merchantId, setMerchantId] = useState<string | undefined>(filters.merchant_id);
  const [clientId, setClientId] = useState<string | undefined>(filters.client_id);
  const [country, setCountry] = useState<string>(filters.country ?? COUNTRY_ARG);

  // Resincroniza con la URL cuando cambia desde afuera (ej. "Limpiar").
  useEffect(() => setFromDate(parseDate(filters.from)), [filters.from]);
  useEffect(() => setToDate(parseDate(filters.to)), [filters.to]);
  useEffect(() => setCarrierId(filters.carrier_id), [filters.carrier_id]);
  useEffect(() => setStatus(filters.status), [filters.status]);
  useEffect(() => setProvince(filters.province), [filters.province]);
  useEffect(() => setMerchantId(filters.merchant_id), [filters.merchant_id]);
  useEffect(() => setClientId(filters.client_id), [filters.client_id]);
  useEffect(() => setCountry(filters.country ?? COUNTRY_ARG), [filters.country]);

  // Al cambiar de país, la provincia seleccionada (de otro país) deja de aplicar:
  // se limpia para no arrastrar un filtro inválido.
  const handleCountryChange = (id: string) => {
    setCountry(id);
    setProvince(undefined);
  };

  const invalidOrder = Boolean(fromDate && toDate && fromDate > toDate);
  const rangeExceeded = Boolean(
    fromDate &&
    toDate &&
    toDate.diff(fromDate, "days").days > MAX_DASHBOARD_RANGE_DAYS,
  );
  const disableApply = !fromDate || !toDate || invalidOrder || rangeExceeded;

  const handleApply = () => {
    if (disableApply || !fromDate || !toDate) return;
    onApply({
      from: fromDate.toISODate() || undefined,
      to: toDate.toISODate() || undefined,
      carrier_id: carrierId,
      status,
      province,
      // Solo se envían cuando el filtro está visible para la entidad.
      merchant_id: showMerchantFilter ? merchantId : undefined,
      client_id: showClientFilter ? clientId : undefined,
      country: showCountryFilter ? country : undefined,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: 2,
        p: 2,
        mb: 3,
        backgroundColor: "transparent",
        borderRadius: 1,
        border: "1px solid #e0e0e0",
      }}
    >
      <Box sx={FIELDS_GRID_SX}>
        <DateRangeFields
          fromDate={fromDate}
          toDate={toDate}
          invalidOrder={invalidOrder}
          rangeExceeded={rangeExceeded}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
        <DimensionFilters
          options={options}
          statusOptions={statusOptions}
          merchantOptions={merchantOptions}
          clientOptions={clientOptions}
          showMerchantFilter={showMerchantFilter}
          showClientFilter={showClientFilter}
          showCountryFilter={showCountryFilter}
          carrierId={carrierId}
          status={status}
          province={province}
          merchantId={merchantId}
          clientId={clientId}
          country={country}
          provinceLabel={provinceLabel}
          onCarrierChange={setCarrierId}
          onStatusChange={setStatus}
          onProvinceChange={setProvince}
          onMerchantChange={setMerchantId}
          onClientChange={setClientId}
          onCountryChange={handleCountryChange}
        />
      </Box>
      <FilterActionButtons
        onClear={onClear}
        onApply={handleApply}
        disableApply={disableApply}
        onExportPdf={onExportPdf}
        exporting={exporting}
      />
      {(invalidOrder || rangeExceeded) && (
        <FormHelperText className="color-error" sx={{ width: "100%", mt: 0 }}>
          {invalidOrder ? ERROR_FROM_GREATER_THAN_TO : ERROR_DASHBOARD_RANGE_EXCEEDED}
        </FormHelperText>
      )}
    </Box>
  );
};

export default DashboardFilterBar;
